import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dn9cspqmi/image/upload';
const UPLOAD_PRESET = 'ml_default';

export interface GuardDocument {
    id: string;
    name: string;
    url: string;
    type: 'APSA' | 'Verification' | 'Other';
    expiryDate?: string;
    uploadedAt: string; // ISO string
    uploadedBy: string;
    fileType?: string;
    fileSize?: number;
}

/**
 * Upload document to Cloudinary
 */
export const uploadDocumentToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);

    const response = await fetch(CLOUDINARY_URL, {
        method: 'POST',
        body: formData,
    });

    const data = await response.json();
    if (data.secure_url) {
        return data.secure_url;
    }
    throw new Error('Document upload failed');
};

/**
 * Add document to guard profile
 */
export const addGuardDocument = async (
    guardId: string,
    file: File,
    type: 'APSA' | 'Verification' | 'Other',
    expiryDate: string,
    documentName: string
): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }

    // Upload document to Cloudinary
    const documentUrl = await uploadDocumentToCloudinary(file);

    const newDocument: GuardDocument = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: documentName || file.name,
        url: documentUrl,
        type,
        expiryDate,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user.uid,
        fileType: file.type,
        fileSize: file.size,
    };

    const guardRef = doc(db, 'guards', user.uid, 'companyGuards', guardId);

    await updateDoc(guardRef, {
        documents: arrayUnion(newDocument)
    });
};

/**
 * Delete document from guard profile
 */
export const deleteGuardDocument = async (
    guardId: string,
    document: GuardDocument
): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }

    const guardRef = doc(db, 'guards', user.uid, 'companyGuards', guardId);

    await updateDoc(guardRef, {
        documents: arrayRemove(document)
    });
};

/**
 * Get guard documents
 * Note: This is usually part of fetching the guard, but can be useful for refreshing just documents
 */
export const getGuardDocuments = async (
    guardId: string
): Promise<GuardDocument[]> => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }

    const guardRef = doc(db, 'guards', user.uid, 'companyGuards', guardId);
    const snap = await getDoc(guardRef);

    if (snap.exists()) {
        return snap.data().documents || [];
    }
    return [];
};
